<template>
  <div class="row col-lg-12 mb-2 tables-filters">
    <div class="col-lg-4 col-md-6 col-sm-12">
      <b-form-group label="Table ID" label-for="filter-tableId">
        <b-form-input
          id="filter-tableId"
          v-model="tableId"
          type="search"
          placeholder="e.g. 1, 5, 10-20"
          autocomplete="off"
          trim
        />
      </b-form-group>
    </div>

    <div class="col-lg-4 col-md-6 col-sm-12">
      <b-form-group label="Table name" label-for="filter-name">
        <b-form-input
          id="filter-name"
          v-model="name"
          type="search"
          placeholder="Search by name"
          autocomplete="off"
          trim
        />
      </b-form-group>
    </div>

    <div class="col-lg-4 col-md-6 col-sm-12">
      <b-form-group label="Game type" label-for="filter-gameType">
        <multiselect
          id="filter-gameType"
          v-model="gameTypes"
          :options="gameTypeOptions"
          label="label"
          track-by="value"
          :multiple="true"
          :close-on-select="false"
          placeholder="Select game types"
        />
      </b-form-group>
    </div>

    <div class="col-lg-4 col-md-6 col-sm-12">
      <b-form-group label="Source" label-for="filter-source">
        <multiselect
          id="filter-source"
          v-model="sources"
          :options="sourceOptions"
          label="label"
          track-by="value"
          :multiple="true"
          :close-on-select="false"
          placeholder="Select sources"
        />
      </b-form-group>
    </div>

    <div class="col-lg-4 col-md-6 col-sm-12">
      <b-form-group label="isVirtual" label-for="filter-isVirtual">
        <multiselect
          id="filter-isVirtual"
          v-model="isVirtual"
          :options="virtualOptions"
          label="label"
          track-by="value"
          :multiple="false"
          :allow-empty="true"
          placeholder="All tables"
        />
      </b-form-group>
    </div>

    <div class="col-lg-4 col-md-6 col-sm-12">
      <b-form-group
        label="Original table"
        label-for="filter-originalTable"
      >
        <b-form-input
          id="filter-originalTable"
          v-model="originalTable"
          type="search"
          placeholder="e.g. 1, 5, 10-20"
          autocomplete="off"
          trim
        />
      </b-form-group>
    </div>

    <div class="col-lg-12 col-md-12 col-sm-12 apply-clear">
      <FilterButtons :loadFn="search" :resetFn="reset" />
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import FilterButtons from '../FilterButtons'

function getEmptyApplied() {
  return {
    tableId: '',
    name: '',
    gameTypes: [],
    sources: [],
    isVirtual: null,
    originalTable: ''
  }
}

function normalizeFilterInput(input) {
  return String(input).replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-')
}

function coerceId(id) {
  if (id === null || id === undefined || id === '') {
    return null
  }
  const num = parseInt(String(id).trim(), 10)
  return Number.isNaN(num) ? null : num
}

function buildIdMatcher(input) {
  const normalized = normalizeFilterInput(input).trim()
  if (!normalized) {
    return null
  }

  const parts = normalized
    .split(',')
    .map(part => part.trim())
    .filter(Boolean)

  const singles = new Set()
  const ranges = []

  for (const part of parts) {
    const range = part.match(/^(\d+)\s*-\s*(\d+)$/)
    if (range) {
      const a = parseInt(range[1], 10)
      const b = parseInt(range[2], 10)
      ranges.push(a > b ? [b, a] : [a, b])
    } else if (/^\d+$/.test(part)) {
      singles.add(parseInt(part, 10))
    }
  }

  if (singles.size === 0 && ranges.length === 0) {
    return () => false
  }

  return id => {
    const num = coerceId(id)
    if (num === null) {
      return false
    }
    if (singles.has(num)) {
      return true
    }
    return ranges.some(([from, to]) => num >= from && num <= to)
  }
}

export default {
  components: {
    Multiselect,
    FilterButtons
  },
  props: {
    items: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      tableId: '',
      name: '',
      gameTypes: [],
      sources: [],
      isVirtual: null,
      originalTable: '',
      applied: getEmptyApplied(),
      virtualOptions: [
        { label: 'Virtual', value: 'virtual' },
        { label: 'Non-virtual', value: 'real' }
      ]
    }
  },
  computed: {
    gameTypeOptions() {
      return this.$store.getters['gameTypes/list'].map(({ id, name }) => ({
        value: id,
        label: name
      }))
    },
    sourceOptions() {
      return this.$store.getters['sourcesModule/list'].map(({ id, name }) => ({
        value: String(id),
        label: name
      }))
    },
    filteredItems() {
      const applied = this.applied
      const idMatcher = buildIdMatcher(applied.tableId)
      const originalMatcher = buildIdMatcher(applied.originalTable)
      const nameQuery = (applied.name || '').toLowerCase()
      const gameTypeIds = applied.gameTypes.map(item => item.value)
      const sourceIds = applied.sources.map(item => String(item.value))
      const virtual = applied.isVirtual ? applied.isVirtual.value : null

      return (this.items || []).filter(item => {
        if (idMatcher && !idMatcher(item.tableId)) {
          return false
        }

        if (nameQuery && !(item.name || '').toLowerCase().includes(nameQuery)) {
          return false
        }

        if (gameTypeIds.length && !gameTypeIds.includes(item.gameType)) {
          return false
        }

        if (sourceIds.length) {
          const itemSources = Array.isArray(item.sourceIds)
            ? item.sourceIds.map(String)
            : []
          if (!sourceIds.some(id => itemSources.includes(id))) {
            return false
          }
        }

        if (virtual === 'virtual' && item.isVirtualTable !== true) {
          return false
        }

        if (virtual === 'real' && item.isVirtualTable === true) {
          return false
        }

        if (originalMatcher && !originalMatcher(item.originalTable)) {
          return false
        }

        return true
      })
    }
  },
  watch: {
    items() {
      this.emitFiltered()
    }
  },
  methods: {
    emitFiltered() {
      this.$emit('filtered', this.filteredItems.slice())
    },
    search() {
      this.applied = {
        tableId: this.tableId,
        name: this.name,
        gameTypes: this.gameTypes.slice(),
        sources: this.sources.slice(),
        isVirtual: this.isVirtual,
        originalTable: this.originalTable
      }
      this.emitFiltered()
    },
    reset() {
      this.tableId = ''
      this.name = ''
      this.gameTypes = []
      this.sources = []
      this.isVirtual = null
      this.originalTable = ''
      this.applied = getEmptyApplied()
      this.emitFiltered()
    }
  },
  mounted() {
    this.$store.dispatch('gameTypes/loadList')
    this.$store.dispatch('sourcesModule/loadList')
    this.emitFiltered()
  }
}
</script>
